import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { TenantConfigProvider } from "@/context/TenantConfigContext";
import AuthCallback from "@/components/AuthCallback";
import { initAttribution } from "@/lib/attribution";
initAttribution();
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import Signup from "@/pages/Signup";
import Demo from "@/pages/Demo";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import Security from "@/pages/Security";
import Benchmarks from "@/pages/Benchmarks";
import Dashboard from "@/pages/Dashboard";
import Accounts from "@/pages/Accounts";
import AccountDetail from "@/pages/AccountDetail";
import WorkQueue from "@/pages/WorkQueue";
import Leaderboard from "@/pages/Leaderboard";
import Legal from "@/pages/Legal";
import Settlements from "@/pages/Settlements";
import Communications from "@/pages/Communications";
import Workflows from "@/pages/Workflows";
import Payments from "@/pages/Payments";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import Tenants from "@/pages/Tenants";
import ImportWizard from "@/pages/ImportWizard";
import Subscription from "@/pages/Subscription";
import EmailLog from "@/pages/EmailLog";
import EmailAutomation from "@/pages/EmailAutomation";
import Leads from "@/pages/Leads";
import LeadIntel from "@/pages/LeadIntel";
import MoneyStuck from "@/pages/MoneyStuck";
import Connect from "@/pages/Connect";
import EmailBranding from "@/pages/EmailBranding";
import EmailTemplates from "@/pages/EmailTemplates";
import EmailRules from "@/pages/EmailRules";
import AdminConnectComms from "@/pages/AdminConnectComms";
import TenantAccounts from "@/pages/TenantAccounts";
import Billing from "@/pages/Billing";
import Providers from "@/pages/Providers";
import Users from "@/pages/Users";
import FieldSettings from "@/pages/FieldSettings";
import IndustryTemplates from "@/pages/IndustryTemplates";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import MetaEvents from "@/pages/MetaEvents";
import WhatsAppIntegration from "@/pages/WhatsAppIntegration";
import ActivationFunnel from "@/pages/ActivationFunnel";
import DataHygiene from "@/pages/DataHygiene";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Onboarding from "@/pages/Onboarding";
import CompleteGoogleProfile from "@/pages/CompleteGoogleProfile";
import Pricing from "@/pages/Pricing";
import { persistFbclid, trackMeta } from "@/lib/meta";
import { ShieldCheck } from "@phosphor-icons/react";
import { IS_PREVIEW } from "@/lib/domains";
import { LimitReachedDialog } from "@/components/LimitReachedDialog";
import ErrorBoundary from "@/components/ErrorBoundary";
import ChatWidget from "@/components/ChatWidget";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Pending() {
  const { logout, user } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 p-6">
      <div className="max-w-md text-center border border-border bg-card p-10">
        <ShieldCheck size={40} weight="fill" className="text-primary mx-auto" />
        <h1 className="text-2xl font-bold mt-4">Access pending</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Your account <strong>{user?.email}</strong> is not yet assigned to a tenant or role. Please ask your
          administrator to grant access.
        </p>
        <button onClick={logout} className="mt-6 text-sm text-primary font-semibold underline" data-testid="pending-logout">
          Sign out
        </button>
      </div>
    </div>
  );
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // Brand-new Google self-signup: authenticated but no workspace yet. Let them provision
  // their tenant in-place (Company Name only) instead of dead-ending on the Pending screen.
  if (user.role === "pending" && !user.tenant_id) return <CompleteGoogleProfile />;
  if (user.role === "pending") return <Pending />;
  return <Layout />;
}

function RootRoute() {
  // On the preview/dev environment, send public visitors to the live site.
  // (Append ?stay=1 to view the preview landing page for QA.)
  if (IS_PREVIEW && !window.location.search.includes("stay=1")) {
    window.location.replace("https://app.recoveros.in/");
    return <Spinner />;
  }
  return <LandingPage />;
}

function SuperAdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function ProtectedBare({ children }) {
  // Auth-guarded but rendered full-screen (no sidebar Layout) — used for the onboarding flow.
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const firstNav = useRef(true);
  useEffect(() => { persistFbclid(); }, []);
  useEffect(() => {
    // Base Pixel fires PageView on first load; track SPA route changes from then on.
    if (firstNav.current) { firstNav.current = false; return; }
    trackMeta("PageView");
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signin" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/security" element={<Security />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/welcome" element={<ProtectedBare><Onboarding /></ProtectedBare>} />
      <Route element={<Protected />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/tenants" element={<SuperAdminOnly><Tenants /></SuperAdminOnly>} />
        <Route path="/tenants/:tenantId/accounts" element={<SuperAdminOnly><TenantAccounts /></SuperAdminOnly>} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/providers" element={<Providers />} />
        <Route path="/accounts" element={<Accounts />} />
        <Route path="/import" element={<ImportWizard />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/email-log" element={<EmailLog />} />
        <Route path="/admin/email-automation" element={<SuperAdminOnly><EmailAutomation /></SuperAdminOnly>} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/lead-intel" element={<LeadIntel />} />
        <Route path="/money-stuck" element={<MoneyStuck />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/connect/email/branding" element={<EmailBranding />} />
        <Route path="/connect/templates/email" element={<EmailTemplates />} />
        <Route path="/connect/email/rules" element={<EmailRules />} />
        <Route path="/accounts/:id" element={<AccountDetail />} />
        <Route path="/work-queue" element={<WorkQueue />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/settlements" element={<Settlements />} />
        <Route path="/communications" element={<Communications />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/benchmarks" element={<Benchmarks />} />
        <Route path="/users" element={<Users />} />
        <Route path="/settings/fields" element={<FieldSettings />} />
        <Route path="/admin/industry-templates" element={<SuperAdminOnly><IndustryTemplates /></SuperAdminOnly>} />
        <Route path="/admin/meta-events" element={<SuperAdminOnly><MetaEvents /></SuperAdminOnly>} />
        <Route path="/admin/whatsapp" element={<SuperAdminOnly><WhatsAppIntegration /></SuperAdminOnly>} />
        <Route path="/admin/connect-communications" element={<SuperAdminOnly><AdminConnectComms /></SuperAdminOnly>} />
        <Route path="/admin/activation-funnel" element={<SuperAdminOnly><ActivationFunnel /></SuperAdminOnly>} />
        <Route path="/admin/data-hygiene" element={<SuperAdminOnly><DataHygiene /></SuperAdminOnly>} />
        <Route path="/audit" element={<AuditLog />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    <ChatWidget />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <TenantConfigProvider>
              <AppRouter />
              <LimitReachedDialog />
              <Toaster position="top-right" richColors />
            </TenantConfigProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
